import { computed, effect, inject, Injectable, PLATFORM_ID, signal } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { DecisionTrees, Trade, Urgency } from '../../../core/models/decision-tree.model';
import { AnfrageOertlicheBedingungen } from '../../../core/models/anfrage.model';
import {
  advanceFromInfoStep, advanceFromSingleSelect, nodeAt, skipUrgencyIfAutoSet,
  walkPath,
} from '../engine/wizard-engine';
import { buildSummaryPreview } from '../engine/summary-preview';

export interface WizardAddress {
  plz: string;
  ort: string;
  strasse?: string;
  hausnummer?: string;
}

const EMPTY_OERTLICHE: AnfrageOertlicheBedingungen = {
  stockwerk: null,
  aufzugStatus: null,
  parkmoeglichkeit: null,
  schmalerTreppenaufgang: false,
  altbau: false,
  denkmalschutz: false,
  mehrfamilienhaus: false,
  gewerbeobjekt: false,
  tierhaltung: false,
  freitextHinweis: null,
};

export interface UploadedPhoto {
  url: string;
  contentType: string;
  groesse: number;
  /** Local-only key; helps the renderer identify the row before the URL exists. */
  clientId: string;
}

interface SerialisedSnapshot {
  v: 1;
  trade: string | null;
  treeVersion: string | null;
  answers: Record<string, string>;
  currentNodeKey: string | null;
  history: string[];
  photos: UploadedPhoto[];
  notes: string;
  address: WizardAddress;
  autoUrgency: Urgency | null;
  oertliche?: AnfrageOertlicheBedingungen;
}

const STORAGE_KEY = 'craftly:anfrage:wizard';

/**
 * Single source of truth for the in-flight Anfrage wizard.
 *
 * Signal-based; persists to sessionStorage on every meaningful mutation so the
 * wizard survives tab reloads and (in Prompt 3) the auth-redirect round trip.
 * Pure traversal logic lives in {@link ../engine/wizard-engine.ts} so it stays
 * unit-testable independent of the framework.
 */
@Injectable({ providedIn: 'root' })
export class AnfrageWizardStore {
  private readonly platformId = inject(PLATFORM_ID);

  // ── Inputs (set on enter()) ────────────────────────────────────────────────
  private readonly _trade = signal<Trade | null>(null);
  private readonly _treeVersion = signal<string | null>(null);

  // ── Mutable state ──────────────────────────────────────────────────────────
  private readonly _answers = signal<Record<string, string>>({});
  private readonly _currentNodeKey = signal<string | null>(null);
  private readonly _history = signal<string[]>([]);
  private readonly _photos = signal<UploadedPhoto[]>([]);
  private readonly _notes = signal<string>('');
  private readonly _address = signal<WizardAddress>({ plz: '', ort: '' });
  private readonly _autoUrgency = signal<Urgency | null>(null);
  private readonly _oertliche = signal<AnfrageOertlicheBedingungen>({ ...EMPTY_OERTLICHE });

  // ── Public read API ────────────────────────────────────────────────────────
  readonly trade = this._trade.asReadonly();
  readonly treeVersion = this._treeVersion.asReadonly();
  readonly answers = this._answers.asReadonly();
  readonly currentNodeKey = this._currentNodeKey.asReadonly();
  readonly history = this._history.asReadonly();
  readonly photos = this._photos.asReadonly();
  readonly notes = this._notes.asReadonly();
  readonly address = this._address.asReadonly();
  readonly autoUrgency = this._autoUrgency.asReadonly();
  readonly oertliche = this._oertliche.asReadonly();

  readonly currentNode = computed(() => {
    const t = this._trade();
    const k = this._currentNodeKey();
    if (!t || !k) return null;
    return t.nodes[k] ?? null;
  });

  readonly canGoBack = computed(() => this._history().length > 0);

  readonly progress = computed(() => {
    const t = this._trade();
    if (!t) return { step: 0, total: 0 };
    const path = walkPath(t, this._answers());
    // total ≈ visible nodes on the chosen path (typically 4-5). step is the
    // index of the current node in that walk; we cap to total for the view.
    const currentKey = this._currentNodeKey();
    const idx = currentKey ? path.indexOf(currentKey) : path.length - 1;
    const total = Math.max(path.length, t.nodes[t.rootNodeId]?.type === 'summary_and_extras' ? 1 : 4);
    const step = idx < 0 ? path.length : idx;
    return { step, total };
  });

  /** Live, FE-only preview. Server is the source of truth at submit time. */
  readonly summaryPreview = computed(() => {
    const t = this._trade();
    if (!t) return '';
    return buildSummaryPreview(t, this._answers(), this.effectiveUrgency());
  });

  constructor() {
    // One-shot rehydrate from sessionStorage. Trade itself is null until enter()
    // resolves it from the loaded spec — we replay the saved answers/etc on top.
    this.tryRestoreFromStorage();

    // Persist on every relevant change. effect() only runs in the browser when
    // the signal updates after construction; the read fan-out is implicit.
    effect(() => {
      this._trade();
      this._answers();
      this._currentNodeKey();
      this._history();
      this._photos();
      this._notes();
      this._address();
      this._autoUrgency();
      this.persist();
    });
  }

  // ── Public command API ─────────────────────────────────────────────────────

  /**
   * Enter or resume a trade. If the trade matches a previously persisted
   * snapshot (same tree version), the saved progress is kept; otherwise the
   * store resets to the trade's root node.
   */
  enter(spec: DecisionTrees, tradeId: string, prefillAddress?: Partial<WizardAddress>): boolean {
    const t = spec.trades[tradeId.toLowerCase()];
    if (!t) return false;

    const restoredTradeId = this.tryRestoreFromStorageWithTrade(spec)?.trade ?? null;
    const sameSession = restoredTradeId === t.id
      && this._treeVersion() === spec.version
      && this._currentNodeKey() != null;

    if (!sameSession) {
      this.reset();
      this._trade.set(t);
      this._treeVersion.set(spec.version);
      this._currentNodeKey.set(t.rootNodeId);
    } else {
      this._trade.set(t);
    }

    if (prefillAddress) {
      this._address.update((a) => ({ ...a, ...prefillAddress }));
    }
    return true;
  }

  select(nodeKey: string, optionValue: string): void {
    const t = this._trade();
    if (!t) return;
    const node = nodeAt(t, nodeKey);
    if (node.type !== 'single_select') return;

    const newAnswers = { ...this._answers(), [node.id]: optionValue };
    const result = advanceFromSingleSelect(t, nodeKey, optionValue);

    let nextKey = result.nextNodeKey;
    let auto = result.autoUrgency ?? this._autoUrgency();

    // Walk through any chained info_steps automatically — they have no UI
    // interaction beyond a "weiter" CTA, but the engine should record their
    // autoSetUrgency before the user lands on the next decision node.
    while (nextKey) {
      const next = t.nodes[nextKey];
      if (!next || next.type !== 'info_step') break;
      const r = advanceFromInfoStep(t, nextKey);
      auto = r.autoUrgency ?? auto;
      nextKey = r.nextNodeKey;
    }

    // If autoSetUrgency was set anywhere on the path, the urgency_ref step
    // is redundant — skip it. The "answers.urgency" value is set by the
    // EFFECTIVE-urgency derivation, not by an explicit user choice.
    if (auto) {
      newAnswers['urgency'] = auto;
      if (nextKey) nextKey = skipUrgencyIfAutoSet(t, nextKey, auto);
    }

    this._history.update((h) => [...h, nodeKey]);
    this._answers.set(newAnswers);
    this._autoUrgency.set(auto);
    this._currentNodeKey.set(nextKey);
  }

  /** Manually advance past an info_step (when the user clicks its CTA). */
  acknowledgeInfo(): void {
    const t = this._trade();
    const k = this._currentNodeKey();
    if (!t || !k) return;
    const node = t.nodes[k];
    if (!node || node.type !== 'info_step') return;

    const r = advanceFromInfoStep(t, k);
    const auto = r.autoUrgency ?? this._autoUrgency();

    let nextKey = r.nextNodeKey;
    if (auto && nextKey) nextKey = skipUrgencyIfAutoSet(t, nextKey, auto);

    this._history.update((h) => [...h, k]);
    if (auto) this._answers.update((a) => ({ ...a, urgency: auto }));
    this._autoUrgency.set(auto);
    this._currentNodeKey.set(nextKey);
  }

  back(): void {
    const hist = this._history();
    if (hist.length === 0) return;
    const previous = hist[hist.length - 1];

    const t = this._trade();
    if (t) {
      // Drop the answer at the node we're returning to so the user can re-pick.
      const previousNode = t.nodes[previous];
      if (previousNode) {
        const updated = { ...this._answers() };
        delete updated[previousNode.id];
        // Going back also unwinds an autoSetUrgency that was set by a deeper choice.
        // Simplest correct approach: drop autoUrgency and let any earlier auto choice
        // re-fire when the user advances forward again.
        delete updated['urgency'];
        this._answers.set(updated);
        this._autoUrgency.set(null);
      }
    }

    this._history.set(hist.slice(0, -1));
    this._currentNodeKey.set(previous);
  }

  setNotes(text: string): void {
    this._notes.set(text);
  }

  setAddress(address: WizardAddress): void {
    this._address.set(address);
  }

  /** Patch einzelne Felder der örtlichen Gegebenheiten (Konzept § 8). */
  patchOertliche(patch: Partial<AnfrageOertlicheBedingungen>): void {
    this._oertliche.update((cur) => ({ ...cur, ...patch }));
  }

  addPhoto(photo: UploadedPhoto): void {
    this._photos.update((list) => [...list, photo]);
  }

  removePhoto(clientId: string): void {
    this._photos.update((list) => list.filter((p) => p.clientId !== clientId));
  }

  reset(): void {
    this._trade.set(null);
    this._treeVersion.set(null);
    this._answers.set({});
    this._currentNodeKey.set(null);
    this._history.set([]);
    this._photos.set([]);
    this._notes.set('');
    this._address.set({ plz: '', ort: '' });
    this._autoUrgency.set(null);
    this._oertliche.set({ ...EMPTY_OERTLICHE });
    if (isPlatformBrowser(this.platformId)) {
      try { sessionStorage.removeItem(STORAGE_KEY); } catch { /* noop */ }
    }
  }

  /** Effective urgency: explicit autoSetUrgency wins over an `urgency` answer. */
  effectiveUrgency(): Urgency | null {
    const auto = this._autoUrgency();
    if (auto) return auto;
    const explicit = this._answers()['urgency'];
    if (explicit === 'notfall' || explicit === 'heute' || explicit === 'diese_woche' || explicit === 'flexibel') {
      return explicit;
    }
    return null;
  }

  /** Snapshot the wizard for cross-route handoff (auth redirect in Prompt 3). */
  snapshot(): SerialisedSnapshot {
    return {
      v: 1,
      trade: this._trade()?.id ?? null,
      treeVersion: this._treeVersion(),
      answers: this._answers(),
      currentNodeKey: this._currentNodeKey(),
      history: this._history(),
      photos: this._photos(),
      notes: this._notes(),
      address: this._address(),
      autoUrgency: this._autoUrgency(),
      oertliche: this._oertliche(),
    };
  }

  // ── Persistence ────────────────────────────────────────────────────────────

  private persist(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    if (!this._trade()) return; // don't write empty snapshots over good ones
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(this.snapshot()));
    } catch { /* quota / disabled — degrade silently */ }
  }

  private tryRestoreFromStorage(): SerialisedSnapshot | null {
    if (!isPlatformBrowser(this.platformId)) return null;
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      const snap = JSON.parse(raw) as SerialisedSnapshot;
      if (snap?.v !== 1) return null;
      this._answers.set(snap.answers ?? {});
      this._currentNodeKey.set(snap.currentNodeKey);
      this._history.set(snap.history ?? []);
      this._photos.set(snap.photos ?? []);
      this._notes.set(snap.notes ?? '');
      this._address.set(snap.address ?? { plz: '', ort: '' });
      this._autoUrgency.set(snap.autoUrgency ?? null);
      this._oertliche.set(snap.oertliche ?? { ...EMPTY_OERTLICHE });
      this._treeVersion.set(snap.treeVersion);
      return snap;
    } catch {
      return null;
    }
  }

  /** Variant of restore that also resolves the trade against the loaded spec. */
  private tryRestoreFromStorageWithTrade(spec: DecisionTrees): SerialisedSnapshot | null {
    if (!isPlatformBrowser(this.platformId)) return null;
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      const snap = JSON.parse(raw) as SerialisedSnapshot;
      if (snap?.v !== 1 || !snap.trade) return null;
      const t = spec.trades[snap.trade.toLowerCase()];
      if (!t) return null;
      // Tree version mismatch? Drop it — the customer's path keys may no longer be valid.
      if (snap.treeVersion && snap.treeVersion !== spec.version) return null;
      this._trade.set(t);
      return snap;
    } catch {
      return null;
    }
  }
}
